import React from "react";

// Normalize translated entries to a consistent shape
export const normalizeEntries = (src, lang, fallbackLang, mapExtraFields) => {
  if (src && Array.isArray(src.entries)) {
    return src.entries.map((entry) => {
      const translated =
        (entry.i18n &&
          (entry.i18n[lang] || entry.i18n[fallbackLang] || entry.i18n.fr)) ||
        {};
      let tags = [];
      if (Array.isArray(translated.tags)) {
        tags = translated.tags;
      } else if (translated.tag) {
        tags = [translated.tag];
      }
      const base = {
        id: entry.id,
        name: translated.name || entry.name || "",
        description: translated.description || "",
        tag: translated.tag,
        tags,
        website: entry.website || null,
        mapsUrl: entry.mapsUrl || null,
        phone: entry.phone || null,
        image: entry.image || null,
      };
      return mapExtraFields
        ? { ...base, ...mapExtraFields(entry, translated) }
        : base;
    });
  }
  const list = (src && (src[lang] || src[fallbackLang] || src.fr)) || [];
  return Array.isArray(list) ? list : [];
};

const collectTags = (items) => {
  const uniqueTags = new Set();
  for (const it of items) {
    if (Array.isArray(it.tags)) {
      for (const tag of it.tags) uniqueTags.add(String(tag));
    } else if (it.tag) {
      uniqueTags.add(String(it.tag));
    }
  }
  return Array.from(uniqueTags).sort((a, b) => a.localeCompare(b));
};

const collectTagCounts = (items) => {
  const counts = Object.create(null);
  for (const it of items) {
    let tags;
    if (Array.isArray(it.tags)) {
      tags = it.tags;
    } else if (it.tag) {
      tags = [it.tag];
    } else {
      tags = [];
    }
    const normalized = Array.from(new Set(tags.map(String)));
    for (const tag of normalized) {
      counts[tag] = (counts[tag] || 0) + 1;
    }
  }
  return counts;
};

const normalizeLabel = (translate, key) => {
  const translateFn = translate || ((x) => x);
  const result = translateFn(key);
  if (typeof result === "string") return result;
  if (Array.isArray(result)) return result.join("");
  return String(result);
};

export const useTagFiltering = ({
  data,
  lang,
  fallbackLang,
  limit,
  translate,
  allLabelKey,
  mapExtraFields,
}) => {
  const items = React.useMemo(
    () => normalizeEntries(data, lang, fallbackLang, mapExtraFields),
    [data, lang, fallbackLang, mapExtraFields]
  );

  const allTags = React.useMemo(() => collectTags(items), [items]);
  const tagCounts = React.useMemo(() => collectTagCounts(items), [items]);
  const totalCount = items.length;
  const [selectedTag, setSelectedTag] = React.useState("");

  const activeTag = React.useMemo(
    () => (selectedTag && allTags.includes(selectedTag) ? selectedTag : ""),
    [selectedTag, allTags]
  );

  const filteredItems = React.useMemo(() => {
    if (!activeTag) return items;
    return items.filter((it) =>
      Array.isArray(it.tags)
        ? it.tags.includes(activeTag)
        : it.tag === activeTag
    );
  }, [items, activeTag]);

  const itemsWithLimit = React.useMemo(() => {
    if (typeof limit === "number" && !activeTag) {
      return filteredItems.slice(0, limit);
    }
    return filteredItems;
  }, [filteredItems, limit, activeTag]);

  const allLabel = React.useMemo(
    () => normalizeLabel(translate, allLabelKey),
    [translate, allLabelKey]
  );

  return {
    items: itemsWithLimit,
    allTags,
    tagCounts,
    totalCount,
    activeTag,
    setSelectedTag,
    allLabel,
  };
};

export default useTagFiltering;
